import { useState } from "react";
import { Button, Col, Collapse, Modal, Row, Table } from "react-bootstrap";
import { ErrorMessage, Formik, Form } from "formik";
import * as Yup from "yup";
import TextError from "../../../layouts/Input/TextError";

export const scrDynamicFieldTypes = [
  { value: 1, label: "Text", icon: "fa-font", tone: "text" },
  { value: 2, label: "Number", icon: "fa-hashtag", tone: "number" },
  { value: 3, label: "Decimal", icon: "fa-percent", tone: "decimal" },
  { value: 4, label: "Date", icon: "fa-calendar-day", tone: "date" },
  { value: 5, label: "DateTime", icon: "fa-clock", tone: "datetime" },
  { value: 6, label: "File", icon: "fa-file-alt", tone: "file" },
  { value: 7, label: "Image", icon: "fa-image", tone: "image" },
];

const emptyField = {
  id: "",
  fieldKey: "",
  label: "",
  fieldType: 1,
  isRequired: false,
  displayOrder: 1,
  isActive: true,
};

const reservedFieldNames = ["Amount", "Cash", "IBFT", "Cheque", "Comment", "Comments"];
const reservedFieldValidationMessage =
  "This is a reserved system field and cannot be added as a dynamic field";

const isReservedFieldName = (value) =>
  reservedFieldNames.some(
    (fieldName) =>
      fieldName.toLowerCase() === (value || "").trim().toLowerCase()
  );

const RequiredIndicator = () => (
  <span className="scr-settings__required-indicator" title="Required">
    *
  </span>
);

const FieldLabel = ({ htmlFor, children, required = false }) => (
  <label htmlFor={htmlFor}>
    {children}
    {required && <RequiredIndicator />}
  </label>
);

const getFieldValidation = (definitions, currentField) =>
  Yup.object().shape({
    fieldKey: Yup.string()
      .matches(
        /^[A-Za-z][A-Za-z0-9_]*$/,
        "Field Key must start with a letter and contain only letters, numbers, or underscore"
      )
      .test(
        "reserved-field-key",
        reservedFieldValidationMessage,
        (value) => !isReservedFieldName(value)
      )
      .test(
        "unique-field-key",
        "Field Key must be unique",
        (value) =>
          !value ||
          !(definitions || []).some(
            (field) =>
              (field.fieldKey || "").toLowerCase() === value.toLowerCase() &&
              (field.id || field.fieldKey) !== (currentField.id || currentField.fieldKey)
          )
      )
      .required("Field Key is required"),
    label: Yup.string()
      .test(
        "reserved-label",
        reservedFieldValidationMessage,
        (value) => !isReservedFieldName(value)
      )
      .test(
        "unique-label",
        "Label must be unique",
        (value) =>
          !value ||
          !(definitions || []).some(
            (field) =>
              (field.label || "").toLowerCase() === value.toLowerCase() &&
              (field.id || field.fieldKey) !== (currentField.id || currentField.fieldKey)
          )
      )
      .required("Label is required")
      .max(100),
    fieldType: Yup.number().required("Field Type is required"),
    displayOrder: Yup.number()
      .typeError("Display Order must be a number")
      .integer("Display Order must be a whole number")
      .min(1, "Display Order minimum value is 1")
      .test(
        "unique-display-order",
        "Display Order must be unique",
        (value) =>
          !value ||
          !(definitions || []).some(
            (field) =>
              Number(field.displayOrder) === Number(value) &&
              (field.id || field.fieldKey) !== (currentField.id || currentField.fieldKey)
          )
      )
      .required("Display Order is required"),
  });

const getFieldTypeMeta = (fieldType) =>
  scrDynamicFieldTypes.find((item) => item.value === Number(fieldType)) ||
  scrDynamicFieldTypes[0];

const FieldTypeBadge = ({ fieldType }) => {
  const meta = getFieldTypeMeta(fieldType);
  return (
    <span className={`scr-settings__field-type-badge scr-settings__field-type-badge--${meta.tone}`}>
      <i className={`fas ${meta.icon}`} aria-hidden="true" />
      {meta.label}
    </span>
  );
};

const PreviewField = ({ field }) => {
  const label = field.label || field.fieldKey;
  const type = Number(field.fieldType);

  if (type === 6 || type === 7) {
    return (
      <div className="form-group mb-2">
        <FieldLabel required={field.isRequired}>{label}</FieldLabel>
        <input className="form-control" type="file" disabled />
      </div>
    );
  }

  return (
    <div className="form-group mb-2">
      <FieldLabel required={field.isRequired}>{label}</FieldLabel>
      <input
        className="form-control"
        type={
          type === 4 || type === 5
            ? "date"
            : type === 2 || type === 3
              ? "number"
              : "text"
        }
        disabled
      />
    </div>
  );
};

const DynamicFieldPreview = ({ definitions, isOpen, onToggle }) => (
  <div className="scr-settings__preview mt-3">
    <div className="scr-settings__modal-section-header">
      <div>
        <b>Form Preview</b>
        <small className="d-block mt-1">Active fields only — how they appear on SCR forms</small>
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline-secondary"
        onClick={onToggle}
        aria-expanded={isOpen}
        style={{ borderRadius: "999px", fontWeight: 600 }}
      >
        <i className={`fas fa-chevron-${isOpen ? "up" : "down"} me-1`} />
        {isOpen ? "Hide" : "Show"}
      </Button>
    </div>
    <Collapse in={isOpen}>
      <div className="scr-settings__modal-section-body">
        {definitions.length === 0 ? (
          <p className="text-muted mb-0">No active dynamic fields configured yet.</p>
        ) : (
          definitions
            .filter((field) => field.isActive !== false)
            .slice()
            .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
            .map((field) => <PreviewField key={field.id || field.fieldKey} field={field} />)
        )}
      </div>
    </Collapse>
  </div>
);

const DynamicFieldBuilder = ({
  definitions,
  readOnly = false,
  onCreate,
  onUpdate,
  onToggleActive,
}) => {
  const [modalShow, setModalShow] = useState(false);
  const [currentField, setCurrentField] = useState(emptyField);
  const [isEdit, setIsEdit] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const orderedDefinitions = (definitions || [])
    .slice()
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

  const openCreate = () => {
    setCurrentField({
      ...emptyField,
      displayOrder: orderedDefinitions.length + 1,
    });
    setIsEdit(false);
    setModalShow(true);
  };

  const openEdit = (field) => {
    setCurrentField({ ...field });
    setIsEdit(true);
    setModalShow(true);
  };

  const handleSubmit = async (values) => {
    const model = {
      id: values.id || undefined,
      fieldKey: values.fieldKey,
      label: values.label,
      fieldType: Number(values.fieldType),
      isRequired: Boolean(values.isRequired),
      displayOrder: Number(values.displayOrder),
    };

    const result = isEdit ? await onUpdate(model) : await onCreate(model);
    if (result) {
      setModalShow(false);
    }
  };

  return (
    <>
      <div className="scr-settings__fields-header">
        <div>
          <b className="scr-settings__fields-title">Dynamic Fields</b>
          <small className="text-muted d-block">
            Custom fields on SCR transaction forms
          </small>
        </div>
        {!readOnly && (
          <Button
            type="button"
            size="sm"
            variant="primary"
            className="scr-settings__primary-button scr-settings__fields-add-btn"
            onClick={openCreate}
          >
            <i className="fa fa-plus me-1" />
            Add
          </Button>
        )}
      </div>

      {orderedDefinitions.length === 0 ? (
        <div className="scr-settings__fields-empty">
          <i className="fas fa-list-ul" aria-hidden="true" />
          <span>
            {readOnly
              ? "No custom fields configured."
              : "No fields yet — add one to collect extra data."}
          </span>
        </div>
      ) : (
        <div className="scr-settings__table-card scr-settings__table-card--compact mt-2 mb-0">
          <Table
            responsive
            hover
            size="sm"
            className={`mb-0 scr-settings__fields-table${
              readOnly ? " scr-settings__fields-table--readonly" : ""
            }`}
          >
            <thead>
              <tr>
                <th>#</th>
                <th>Label</th>
                <th>Field Key</th>
                <th>Type</th>
                <th>Required</th>
                <th>Order</th>
                <th>Status</th>
                {!readOnly && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {orderedDefinitions.map((field, index) => (
                <tr key={field.id || field.fieldKey}>
                  <td>{index + 1}</td>
                  <td>
                    <strong title={field.label || ""}>{field.label}</strong>
                  </td>
                  <td>
                    <code title={field.fieldKey || ""}>{field.fieldKey}</code>
                  </td>
                  <td>
                    <FieldTypeBadge fieldType={field.fieldType} />
                  </td>
                  <td>
                    <span
                      className={`scr-settings__pill ${
                        field.isRequired
                          ? "scr-settings__pill--accent"
                          : "scr-settings__pill--muted"
                      }`}
                    >
                      {field.isRequired ? "Required" : "Optional"}
                    </span>
                  </td>
                  <td>{field.displayOrder}</td>
                  <td>
                    <span
                      className={`scr-settings__pill ${
                        field.isActive !== false
                          ? "scr-settings__pill--success"
                          : "scr-settings__pill--warning"
                      }`}
                    >
                      {field.isActive !== false ? "Active" : "Inactive"}
                    </span>
                  </td>
                  {!readOnly && (
                    <td>
                      <div className="scr-settings__action-group">
                        <button
                          type="button"
                          onClick={() => openEdit(field)}
                          className="scr-settings__icon-btn scr-settings__icon-btn--edit"
                          title="Edit field"
                        >
                          <i className="fas fa-edit" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onToggleActive(field)}
                          className={`scr-settings__icon-btn scr-settings__icon-btn--toggle ${
                            field.isActive !== false ? "is-active" : "is-inactive"
                          }`}
                          title={field.isActive !== false ? "Deactivate field" : "Activate field"}
                        >
                          <i
                            className={
                              field.isActive !== false
                                ? "fas fa-toggle-on"
                                : "fas fa-toggle-off"
                            }
                          />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <DynamicFieldPreview
        definitions={orderedDefinitions}
        isOpen={isPreviewOpen}
        onToggle={() => setIsPreviewOpen((current) => !current)}
      />

      <Modal
        show={modalShow}
        onHide={() => setModalShow(false)}
        backdrop="static"
        size="lg"
        centered
        className="scr-settings__type-modal"
      >
        <Modal.Header closeButton className="scr-settings__type-modal-header">
          <div className="scr-settings__type-modal-title-wrap">
            <div className="scr-settings__type-modal-icon scr-settings__type-modal-icon--adjustment">
              <i className="fas fa-list-ul" aria-hidden="true" />
            </div>
            <div className="scr-settings__type-modal-heading">
              <Modal.Title>{isEdit ? "Edit Field" : "Add Field"}</Modal.Title>
            </div>
          </div>
        </Modal.Header>
        <Formik
          initialValues={currentField}
          enableReinitialize
          validationSchema={getFieldValidation(definitions, currentField)}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, handleChange, isSubmitting }) => (
            <Form autoComplete="off">
              <Modal.Body className="scr-settings__type-modal-body">
                <div className="scr-settings__form-card mb-0">
                  <Row className="g-2">
                    <Col md={6} className="scr-settings__field-group scr-settings__field-group--compact">
                      <FieldLabel htmlFor="fieldKey" required>
                        Field Key
                      </FieldLabel>
                      <input
                        id="fieldKey"
                        name="fieldKey"
                        className="form-control"
                        type="text"
                        autoComplete="off"
                        disabled={isEdit}
                        value={values.fieldKey || ""}
                        onChange={handleChange}
                        placeholder="e.g. referenceNo"
                      />
                      <ErrorMessage component={TextError} name="fieldKey" />
                    </Col>
                    <Col md={6} className="scr-settings__field-group scr-settings__field-group--compact">
                      <FieldLabel htmlFor="label" required>
                        Label
                      </FieldLabel>
                      <input
                        id="label"
                        name="label"
                        className="form-control"
                        type="text"
                        autoComplete="off"
                        value={values.label || ""}
                        onChange={handleChange}
                        placeholder="Display label"
                      />
                      <ErrorMessage component={TextError} name="label" />
                    </Col>
                    <Col md={4} className="scr-settings__field-group scr-settings__field-group--compact">
                      <FieldLabel htmlFor="fieldType" required>
                        Type
                      </FieldLabel>
                      <select
                        id="fieldType"
                        name="fieldType"
                        className="form-select"
                        value={values.fieldType}
                        onChange={(event) =>
                          setFieldValue("fieldType", Number(event.target.value))
                        }
                      >
                        {scrDynamicFieldTypes.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </Col>
                    <Col md={4} className="scr-settings__field-group scr-settings__field-group--compact">
                      <FieldLabel htmlFor="displayOrder" required>
                        Order
                      </FieldLabel>
                      <input
                        id="displayOrder"
                        name="displayOrder"
                        className="form-control"
                        type="number"
                        min={1}
                        step={1}
                        autoComplete="off"
                        value={values.displayOrder ?? ""}
                        onChange={(event) => {
                          const raw = event.target.value;
                          if (raw === "") {
                            setFieldValue("displayOrder", "");
                            return;
                          }
                          const next = Number(raw);
                          if (Number.isNaN(next)) {
                            return;
                          }
                          setFieldValue("displayOrder", next < 1 ? 1 : next);
                        }}
                        placeholder="1"
                      />
                      <ErrorMessage component={TextError} name="displayOrder" />
                    </Col>
                    <Col md={4} className="scr-settings__field-group scr-settings__field-group--compact mb-0">
                      <FieldLabel htmlFor="scr-field-required">Required</FieldLabel>
                      <div className="scr-settings__segmented scr-settings__segmented--inline">
                        <button
                          type="button"
                          id="scr-field-required"
                          className={`scr-settings__segmented-item ${
                            values.isRequired ? "is-active" : ""
                          }`}
                          onClick={() => setFieldValue("isRequired", true)}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          className={`scr-settings__segmented-item ${
                            !values.isRequired ? "is-active" : ""
                          }`}
                          onClick={() => setFieldValue("isRequired", false)}
                        >
                          No
                        </button>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Modal.Body>
              <Modal.Footer className="scr-settings__modal-footer">
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={() => setModalShow(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  className="scr-settings__primary-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : isEdit ? "Save Field" : "Add Field"}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};

export default DynamicFieldBuilder;
